import {useMutation} from "react-query";
import caseService, {
    FetchCaseActivitiesResponse,
    SeekAdviceParams
} from "../../services/caseService";
import queryKeys from "../../constants/queryKeys";
import reactQueryUtils from "../../utils/reactQueryUtils";
import {nanoid} from "@reduxjs/toolkit";
import {MISC} from "../../constants/caseActivityTypes";
import {directions} from "../../constants/directions";
import {useAuth} from "../../contexts/authContext";
import {getFileExtension} from "../../utils/fileNameUtils";
import {useSnackbar} from "notistack";
import {MiscActivityCategory} from "../../constants/miscActivityCategory";

const useSeekAdvice = () => {
    const { user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: (query: SeekAdviceParams) => caseService.seekAdvice(query),
        onMutate: async (query: SeekAdviceParams) => {
            const queryKey = [queryKeys.caseActivities, query.caseId];
            reactQueryUtils.client.cancelQueries(queryKey);
            const previousActivities = reactQueryUtils.client.getQueryData(queryKey);
            if (previousActivities)
                reactQueryUtils.client.setQueryData(queryKey, (prev: FetchCaseActivitiesResponse) => ({
                    ...prev,
                    caseActivities: [
                        {
                            caseId: query.caseId,
                            caseActivityId: nanoid(),
                            isSyncing: true,
                            itemType: MISC,
                            eventTime: new Date(),
                            content: query.message,
                            direction: directions.OTHER,
                            person: user?.details.firstName + " " + user?.details.lastName,
                            attachments: query.attachments?.map(x => ({
                                reference: x.id,
                                filename: x.file?.name,
                                contentType: x.file?.type,
                                extension: getFileExtension(x.file?.name || "").replaceAll(".", "")
                            })),
                            miscActivityTypeDescription: "Seek Advice",
                            miscActivityCategory: MiscActivityCategory.SeekAdvice,
                            activityDescription: query.subject
                        },
                        ...prev.caseActivities
                    ]
                }));
            return { previousActivities, queryKey };
        },
        onError: (err, variables, context) => {
            enqueueSnackbar("Failed to Seek Advice", { variant: "error" });
            if (context?.previousActivities)
                reactQueryUtils.client.setQueryData(context.queryKey, context.previousActivities);
        },
        onSuccess: () => { enqueueSnackbar("Seek Advice request submitted. Your query will be responded to within the agreed SLA period.") },
        onSettled: (data, err, newDetails, context) => {
            if (context?.queryKey)
                reactQueryUtils.client.invalidateQueries(context.queryKey);
        },
    });
}


export default useSeekAdvice;